import { z } from 'zod/v4';

export const AUDITS_LIST_DEFAULT_PAGE_SIZE = 15;
export const AUDITS_LIST_MAX_PAGE_SIZE = 500;
export const AUDITS_LIST_FILTER_MAX_LENGTH = 512;

/**
 * Sort columns the audits list accepts — mirrors `SORT_COLUMNS_ENUM` in the
 * legacy `audits-service/routes/schemas/v1/request/audits.js`. Each key maps
 * to a SQL expression in `core/db/audits/filters.ts` (`SORT_COLUMNS`); keep
 * the two in sync when adding or removing a column.
 */
export const AuditsListSortColumns = [
  'updatedAt',
  'name',
  'type',
  'referenceNumber',
  'companyName',
  'uploaderCompanyName',
  'facilityScore',
  'socialScore',
  'auditedBy',
  'auditDate',
  'nextPlannedAuditDate',
  'facilityProcesses',
  'auditReportFileId',
  'dataStatus',
  'aiActions',
  'tierLevel',
  'collaborationStatus',
  'country',
  'agent',
  'vendor',
  'internalCompanyCode',
  'notes',
] as const;

export type AuditsListSortColumn = (typeof AuditsListSortColumns)[number];

/**
 * `column:DIRECTION` pairs, comma-separated, columns constrained to the sort
 * tuple — the legacy AJV pattern rejected unknown columns with a 400, so the
 * port does too. Lowercase directions are accepted as a superset — the
 * builder upper-cases before interpolating.
 */
const sortColumnAlternatives = AuditsListSortColumns.join('|');
const sortParamSchema = z
  .string()
  .regex(
    new RegExp(
      `^((${sortColumnAlternatives}):(ASC|DESC|asc|desc))(,((${sortColumnAlternatives}):(ASC|DESC|asc|desc)))*$`,
    ),
  )
  .optional();

/**
 * Fastify coerces `?flag=true` into a real boolean, but callers building the
 * query string with `URLSearchParams` send the literal string. Accept both,
 * matching how the legacy JSON-Schema `boolean` behaved in practice.
 */
const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => v === true || v === 'true');

/**
 * Like `booleanLike`, but an ABSENT param stays `undefined` instead of
 * collapsing to `false`. The legacy handler triggers `isForNetwork` on
 * PRESENCE (`!== undefined`), not truthiness — `isForNetwork=false` still
 * excludes the viewer's own company — so the schema must preserve absence.
 */
const presenceBooleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => (v === undefined ? undefined : v === true || v === 'true'));

/**
 * Query for `GET /api/audits` — legacy param names verbatim.
 *
 * Deliberately UNDECLARED (zod strips unknown query keys, matching the legacy
 * AJV `removeAdditional` behaviour): `withAllStatus` (destructured by the
 * legacy route but stripped by AJV before it ever arrived — fe-v2 sends it on
 * every call for nothing), `q` and `entity` (declared in the legacy schema
 * but never read by the query), and `isDropDown` (accepted and ignored).
 *
 * `expirationStatus` was ALSO stripped by the legacy AJV schema (the SQL
 * branch for it was unreachable in production). It is declared here with the
 * B5 totality fix — `NON_EXPIRED` matches `>=` start-of-today, so an audit
 * expiring today matches exactly one of the two values. Zero parity risk: no
 * dashboard sends the param.
 */
export const AuditsListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(AUDITS_LIST_MAX_PAGE_SIZE)
    .default(AUDITS_LIST_DEFAULT_PAGE_SIZE),
  sort: sortParamSchema,
  filter: z.string().max(AUDITS_LIST_FILTER_MAX_LENGTH).optional(),
  search: z
    .string()
    .max(255)
    .optional()
    .describe(
      'Free-text search over the audit name, reference number, audited company, uploader company and auditor, as a case-insensitive substring; `%`, `_` and `\\` match literally. Ignored under three letters or digits.',
    ),
  companyId: z.string().max(36).optional(),
  isForNetwork: presenceBooleanLike,
  isCompanyProfile: booleanLike,
  expirationStatus: z.enum(['EXPIRED', 'NON_EXPIRED']).optional(),
});

export type AuditsListQuery = z.infer<typeof AuditsListQuerySchema>;

/**
 * One audits-list row on the wire.
 *
 * `z.looseObject` — the SQL returns ~45 columns and undeclared ones ride
 * along untouched (`count` from `COUNT(*) OVER()`, the file
 * content-type/bucket columns). Declared fields are what the frontends
 * actually read — **this schema is the frontends' type for these rows**; a
 * field the FE reads must be declared here or it infers as `unknown`.
 *
 * Censoring (company-profile rows the viewer has no `rights` on) masks
 * string values to `'*'`-runs and non-strings to `''`/`null`, so every
 * maskable field stays `z.string().nullable()` — never `z.iso.datetime()`.
 * `agents`/`vendors`/`tierLevel` are JSON **strings** on the wire (the
 * legacy serialiser coerced the jsonb values; fe-v2 `JSON.parse`s them).
 * `correctiveActionPlanFileFileType` keeps the legacy double-"File" alias
 * verbatim — the old FE reads it.
 */
export const AuditListRowSchema = z.looseObject({
  // Present on every row — id/companyId are censor-exempt, isPublic, rights
  // and requestStatus are emitted by both the censored and the normal branch.
  id: z.string(),
  companyId: z.string(),
  isPublic: z.boolean(),
  rights: z.array(z.string()).nullable(),
  requestStatus: z.string().nullable(),
  name: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  auditTypeId: z.string().nullable().optional(),
  auditTypeName: z.string().nullable().optional(),
  referenceNumber: z.string().nullable().optional(),
  companyName: z.string().nullable().optional(),
  uploaderCompanyId: z.string().nullable().optional(),
  uploaderCompanyName: z.string().nullable().optional(),
  facilityScore: z.string().nullable().optional(),
  auditDate: z.string().nullable().optional(),
  auditedBy: z.string().nullable().optional(),
  nextPlannedAuditDate: z.string().nullable().optional(),
  privacyStatus: z
    .object({ status: z.enum(['public', 'private']) })
    .nullable()
    .optional(),
  auditReportFileId: z.string().nullable().optional(),
  auditReportFileUrl: z.string().nullable().optional(),
  auditReportFileType: z.string().nullable().optional(),
  auditReportFileOriginalUrl: z.string().nullable().optional(),
  correctiveActionPlanFileId: z.string().nullable().optional(),
  correctiveActionPlanFileUrl: z.string().nullable().optional(),
  correctiveActionPlanFileFileType: z.string().nullable().optional(),
  correctiveActionPlanFileOriginalUrl: z.string().nullable().optional(),
  requestCompanyFacilityProcesses: z.string().nullable().optional(),
  tierLevel: z.string().nullable().optional(),
  collaborationStatus: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  agents: z.string().nullable().optional(),
  vendors: z.string().nullable().optional(),
  internalCompanyCode: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),
  updatedByCompanyName: z.string().nullable().optional(),
  generatedBy: z.string().nullable().optional(),
  externalSource: z.string().nullable().optional(),
  digitizationStatus: z.string().nullable().optional(),
  digitizationPercentage: z.number().nullable().optional(),
  /** Retraced Social Score of the newest COMPLETED run; null when the audit
   *  has none, or its only runs are pending or failed. */
  socialScore: z.number().nullable().optional(),
});

export type AuditListRow = z.infer<typeof AuditListRowSchema>;

export const AuditsListResponseSchema = z.object({
  data: z.array(AuditListRowSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type AuditsListResponse = z.infer<typeof AuditsListResponseSchema>;
